import { FormControlLabel, Radio, RadioGroup, useMediaQuery } from '@mui/material';
import { isToday } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StorePerformanceInterval, StorePerformanceIntervalMap } from '../../constants';
import { LocalStorageKey } from '../../constants/localStorageKey';
import { PickerType, StoreDetails } from '../../global-state/types';
import { useLocalStorage, useStoreInformation, useUser } from '../../hooks';
import { Container, Icons, Typography } from '../../stories/atoms';
import { ContainerV2, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { Search } from '../../stories/molecules';
import { DatePicker } from '../../stories/molecules/date-picker/DatePicker';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { homeTranslations, selectStoreTranslation } from '../../translations';
import { getDatePickerLabel } from '../../utils';
import { StyledContainer, StyledIconWithText } from './AreaOverview.styles';
import { AreaWidgetsContainer } from './areaWidgetsContainer/AreaWidgetsContainer';

export const AreaOverview: FC = () => {
  const { t } = useTranslation();
  const { datePicker, common } = homeTranslations;
  const { areaName } = useStoreInformation();
  const { setItem } = useLocalStorage();
  const navigate = useNavigate();

  const {
    get: { storesDetails, userPermissions },
    set,
  } = useUser();

  const [selectedStore, setSelectedStore] = useState<StoreDetails | null>(null);

  // Datepicker states
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pickerType, setPickerType] = useState<PickerType>('date');
  const [currentInterval, setCurrentInterval] = useState<StorePerformanceInterval>(StorePerformanceInterval.DAILY);
  const [dateLabel, setDateLabel] = useState(
    getDatePickerLabel(
      selectedDate,
      currentInterval,
      t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
    ),
  );

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isCurrentDay = isToday(selectedDate);

  const onStoreSelect = () => {
    if (!selectedStore) return;
    setItem(LocalStorageKey.SelectedStore, selectedStore?.storeId);
    setItem(LocalStorageKey.SelectedView, 'store');
    set({ currentStoreId: selectedStore?.storeId });
    navigate('/');
  };

  const filteredStores = storesDetails.filter((store) => store.area === areaName);
  const openStores = filteredStores.filter((store) => store.status === 'OPEN');

  const handleSearch = (selectedValue: StoreDetails | null) => {
    if (selectedValue) {
      setSelectedStore(selectedValue);
    } else {
      setItem(LocalStorageKey.SelectedView, 'area');
      setSelectedStore(null);
    }
  };

  const customFilterFunction = (options: StoreDetails[], inputValue: string) => {
    return options.filter(
      (option) =>
        option.storeId.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.name.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const isSearchButtonDisabled = !selectedStore;

  const isWeeklyAndMonthlyActive = userPermissions.areaResultPage.canRead;

  return (
    <Page
      header={
        <RowFlexContainer
          justifyContent="between"
          alignItems="center"
          height="100%"
          padding={isMobile ? [0, 4] : [0, 8]}
        >
          <RowFlexContainer gap={[1]}>
            <ContainerV2 width={isMobile ? '150px' : '250px'}>
              <Search
                fieldLabel={t(
                  selectStoreTranslation.selectStore.key,
                  selectStoreTranslation.selectStore.defaultValue,
                )}
                options={openStores}
                getOptionLabel={(store) => `${store.storeId} - ${store.name}`}
                onSelect={handleSearch}
                filterFunction={customFilterFunction}
              />
            </ContainerV2>
            <StyledIconWithText
              backgroundColor="primary"
              padding={[2]}
              data-testid="select-store-button"
              iconOptions={{
                icon: Icons.SEARCH,
                size: 'small',
                color: 'white',
              }}
              isDisabled={isSearchButtonDisabled}
              onClick={onStoreSelect}
            />
          </RowFlexContainer>
          <DatePicker
            inputSelectedDate={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              const interval = StorePerformanceIntervalMap[pickerType];
              setCurrentInterval(interval);
              setDateLabel(
                getDatePickerLabel(date, interval, t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue)),
              );
            }}
            label={dateLabel}
            pickerType={pickerType}
            views={pickerType === 'month' ? ['year', 'month'] : ['day']}
            showCalendarIcon
            actionBar={
              <RadioGroup
                aria-labelledby="date-picker-radio-group"
                value={pickerType}
                onChange={(event) => setPickerType(event.target.value as 'date')}
                sx={{ display: 'flex', py: 4, px: 4 }}
              >
                <FormControlLabel
                  value="date"
                  control={<Radio />}
                  label={t(datePicker.radioLabel1.key, datePicker.radioLabel1.defaultValue)}
                />
                {isWeeklyAndMonthlyActive && (
                  <FormControlLabel
                    value="week"
                    control={<Radio />}
                    label={t(datePicker.radioLabel2.key, datePicker.radioLabel2.defaultValue)}
                  />
                )}

                {isWeeklyAndMonthlyActive && (
                  <FormControlLabel
                    value="month"
                    control={<Radio />}
                    label={t(datePicker.radioLabel3.key, datePicker.radioLabel3.defaultValue)}
                  />
                )}
              </RadioGroup>
            }
          />
        </RowFlexContainer>
      }
      body={
        <div>
          <Container padding={isMobile ? [4, 5] : [4, 8]} wrap="nowrap" data-testid="area-overview-page">
            <Container
              direction={isMobile ? 'vertical' : 'horizontal'}
              wrap="nowrap"
              space="between"
              position={undefined}
            >
              <Container wrap="nowrap">
                <Typography type="h3" data-testid="area-name" overflow="visible">
                  {areaName}
                </Typography>
                <Typography
                  type="body3"
                  data-testid={!isCurrentDay ? 'yesterday-date' : 'selected-date'}
                  overflow="visible"
                >
                  {t(common.overview.key, common.overview.defaultValue)}
                  {' - '}
                  {dateLabel}
                </Typography>
              </Container>
            </Container>
            <StyledContainer data-testid="area-widgets-container">
              <AreaWidgetsContainer interval={currentInterval} currentDate={selectedDate} />
            </StyledContainer>
          </Container>
        </div>
      }
    />
  );
};
